/**
 * My Classes – a teacher's workspace: students waiting to join their classes,
 * the classes they teach (and ones shared with them), and their students.
 *
 * Replaces the teacher's view of the old Class Management page and the old
 * /teacher/approvals page. The school creates classes; a teacher approves
 * students into, and manages the roster of, the classes they teach. Every
 * change goes through /school-roster, which enforces exactly that.
 */

import { useMemo, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { learnXRFontStyle } from '../../Components/LearnXRTypography';
import { AnalyticsEmptyState, AnalyticsErrorState, AnalyticsPanel, TableSkeleton } from '../../Components/analytics/AnalyticsPanel';
import { DataTable, type Column } from '../../Components/analytics/DataTable';
import { Badge } from '../../Components/ui/badge';
import { ClassDrawer, classLabel } from '../../Components/classroom/ClassDrawer';
import { TeacherApprovalsTab } from '../../Components/classroom/TeacherApprovalsTab';
import { useSchoolRoster, type RosterPerson } from '../../hooks/useSchoolRoster';
import { MAX_CLASS_SIZE } from '../../services/classManagementService';
import { pendingForTeacher } from '../../lib/classroom/students';
import type { Class } from '../../types/lms';

const TABS = [
  { key: 'approvals', label: 'Approvals' },
  { key: 'classes', label: 'My classes' },
  { key: 'students', label: 'Students' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

const TeacherClassroom = () => {
  const { profile } = useAuth();
  const [params, setParams] = useSearchParams();
  const uid = profile?.uid ?? '';
  const schoolId = profile?.school_id ?? null;
  const approved = profile?.approvalStatus === 'approved';

  const roster = useSchoolRoster(approved ? schoolId : null);
  const mine = useMemo(() => roster.classes.filter((c) => (c.teacher_ids ?? []).includes(uid)), [roster.classes, uid]);
  const shared = useMemo(
    () => roster.classes.filter((c) => !(c.teacher_ids ?? []).includes(uid) && (c.shared_with_teachers ?? []).includes(uid)),
    [roster.classes, uid]
  );
  const myIds = useMemo(() => mine.map((c) => c.id), [mine]);
  const pending = useMemo(() => pendingForTeacher(roster.students, myIds), [roster.students, myIds]);

  // Open on the decisions waiting, else the classes.
  const requested = TABS.find((t) => t.key === params.get('tab'))?.key;
  const tab: TabKey = requested ?? (pending.length ? 'approvals' : 'classes');
  const openId = params.get('open');
  const setParam = (key: 'tab' | 'open', value: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (value) n.set(key, value);
        else n.delete(key);
        if (key === 'tab') n.delete('open');
        // Pin the tab a drawer was opened from; the default tab can change underneath it.
        else n.set('tab', tab);
        return n;
      },
      { replace: true }
    );

  const open = openId ? roster.classById.get(openId) ?? null : null;
  const openIsMine = !!open && myIds.includes(open.id);

  if (!approved || !schoolId) {
    return (
      <Shell>
        <AnalyticsPanel title="Not available yet">
          <AnalyticsEmptyState
            title={schoolId ? 'Your account is waiting for your school to approve it' : 'Your account is not linked to a school'}
            description="Once your school approves you and gives you classes, they appear here."
          />
        </AnalyticsPanel>
      </Shell>
    );
  }

  const classColumns: Column<Class>[] = [
    {
      key: 'name',
      header: 'Class',
      sortValue: (c) => classLabel(c),
      cell: (c) => (
        <div className="min-w-0">
          <p className="flex items-center gap-2 truncate font-medium text-foreground">
            {classLabel(c)}
            {!myIds.includes(c.id) && <Badge variant="outline">Shared with you</Badge>}
          </p>
          <p className="truncate text-xs text-muted-foreground">{[c.subject, c.academic_year].filter(Boolean).join(' · ')}</p>
        </div>
      ),
    },
    {
      key: 'lead',
      header: 'Class teacher',
      hideOnMobile: true,
      cell: (c) =>
        c.class_teacher_id === uid ? (
          <span className="text-foreground">You</span>
        ) : (
          <span className="text-muted-foreground">{c.class_teacher_id ? roster.teacherById.get(c.class_teacher_id)?.name ?? '—' : 'Not set'}</span>
        ),
    },
    {
      key: 'waiting',
      header: 'Waiting',
      align: 'right',
      sortValue: (c) => pending.filter((s) => (s.requestedClassId ?? s.classIds[0]) === c.id).length,
      cell: (c) => {
        const n = pending.filter((s) => (s.requestedClassId ?? s.classIds[0]) === c.id).length;
        return n ? <span className="font-medium text-amber-600 dark:text-amber-400">{n}</span> : <span className="text-muted-foreground">0</span>;
      },
    },
    {
      key: 'students',
      header: 'Students',
      align: 'right',
      sortValue: (c) => (c.student_ids ?? []).length,
      cell: (c) => (
        <span className="tabular-nums text-foreground">
          {(c.student_ids ?? []).length}/{MAX_CLASS_SIZE}
        </span>
      ),
    },
  ];

  const myStudents = roster.students.filter((s) => s.classIds.some((id) => myIds.includes(id)));
  const studentColumns: Column<RosterPerson>[] = [
    {
      key: 'name',
      header: 'Student',
      sortValue: (s) => s.name,
      cell: (s) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{s.name}</p>
          <p className="truncate text-xs text-muted-foreground">{s.email}</p>
        </div>
      ),
    },
    {
      key: 'classes',
      header: 'Class',
      sortValue: (s) => s.classIds.filter((id) => myIds.includes(id)).map((id) => classLabel(roster.classById.get(id)!)).join(', '),
      cell: (s) => (
        <span className="text-muted-foreground">
          {s.classIds.filter((id) => myIds.includes(id)).map((id) => classLabel(roster.classById.get(id)!)).join(', ')}
        </span>
      ),
    },
  ];

  return (
    <Shell>
      <nav role="tablist" aria-label="My classes sections" className="flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setParam('tab', t.key)}
            className={`-mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition ${
              tab === t.key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
            {t.key === 'approvals' && pending.length > 0 && (
              <span className="rounded-full bg-amber-500/15 px-1.5 text-[11px] font-semibold tabular-nums text-amber-600 dark:text-amber-400">
                {pending.length}
              </span>
            )}
          </button>
        ))}
      </nav>

      {roster.error && <AnalyticsErrorState message={roster.error} />}

      {tab === 'approvals' ? (
        <TeacherApprovalsTab roster={roster} myClassIds={myIds} />
      ) : tab === 'classes' ? (
        <AnalyticsPanel title="My classes" description="Classes you teach, and classes other teachers have shared with you. Your school creates classes.">
          {roster.loading ? (
            <TableSkeleton />
          ) : (
            <DataTable
              rows={[...mine, ...shared]}
              columns={classColumns}
              rowKey={(c) => c.id}
              initialSort={{ key: 'name', direction: 'asc' }}
              onRowClick={(c) => setParam('open', c.id)}
              empty={
                <AnalyticsEmptyState compact title="No classes yet" description="Your school assigns you to classes; they appear here once it does." />
              }
            />
          )}
        </AnalyticsPanel>
      ) : (
        <AnalyticsPanel title="Students" description={`${myStudents.length} student(s) across your classes.`}>
          {roster.loading ? (
            <TableSkeleton />
          ) : (
            <DataTable
              rows={myStudents}
              columns={studentColumns}
              rowKey={(s) => s.id}
              initialSort={{ key: 'name', direction: 'asc' }}
              searchPlaceholder="Search name or email"
              searchText={(s) => `${s.name} ${s.email}`}
              empty={<AnalyticsEmptyState compact title="No students yet" description="Approve students, or add them to a class from its page." />}
            />
          )}
        </AnalyticsPanel>
      )}

      {open && (
        <ClassDrawer
          key={open.id}
          cls={open}
          roster={roster}
          schoolId={schoolId}
          mode="teacher"
          readOnly={!openIsMine}
          moveTargets={mine}
          onClose={() => setParam('open', null)}
        />
      )}
    </Shell>
  );
};

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-background pb-12 pt-24">
      <div className="mx-auto max-w-6xl space-y-5 px-4 sm:px-6">
        <header>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Teaching</p>
          <h1 className="mt-1 text-2xl font-bold text-foreground" style={learnXRFontStyle}>
            My Classes
          </h1>
        </header>
        {children}
      </div>
    </div>
  );
}

export default TeacherClassroom;
